import { isUsingNewComponents } from "../add-element/utils";
import { FRONTEND_FLAGS } from "../core/components/LaunchDarklyProvider";
import { OnlineInvitationSite, Site } from "../resources/modelTypes";
import { TestValues } from "../resources/modelTypes";
import { useMediaQueryState } from "@minted/minted-components";
import config from "config";
import { useLDClient } from "launchdarkly-react-client-sdk";
import React, { createContext, ReactNode, useContext, useMemo } from "react";

export type StaticSiteData = {
  createdAt: string;
  id: number;
  isMobile: boolean;
  isOIMessagesTabEnabled: boolean;
  isOnlineInvitationSite: boolean;
  isSuiteUpsellEnabled: boolean;
  isUsingNewComponents: boolean;
  isUsingNewLinkRegistryFlow: boolean;
  isUsingStripeRegistries: boolean;
  isConnectingHouseholdMembers: boolean;
  isWeddingSite: boolean;
  siteType: string;
  lastNewContactsBannerDismissedDate: string | null;
};

export const StaticSiteDataContext = createContext<StaticSiteData>({
  createdAt: "2000-01-01T00:00:00.000000",
  id: 0,
  isMobile: false,
  isOIMessagesTabEnabled: false,
  isOnlineInvitationSite: false,
  isSuiteUpsellEnabled: false,
  isUsingNewComponents: false,
  isUsingNewLinkRegistryFlow: false,
  isUsingStripeRegistries: false,
  isConnectingHouseholdMembers: false,
  isWeddingSite: false,
  siteType: "",
  lastNewContactsBannerDismissedDate: null,
});

export type StaticSiteDataProviderProps = {
  children: ReactNode;
  site: Site;
};

export const StaticSiteDataProvider = ({
  children,
  site,
}: StaticSiteDataProviderProps) => {
  const mediumMediaQueryState = useMediaQueryState({
    mediaQuerySize: "medium",
  });
  const isMobile: boolean = mediumMediaQueryState === "BELOW";

  const providerValue = useMemo(
    () => ({
      createdAt: site.createdAt,
      id: site.id,
      isMobile,
      isOIMessagesTabEnabled:
        (site as OnlineInvitationSite).siteDetails?.oiMessagesTab ===
        TestValues.Experiment,
      isOnlineInvitationSite: site.isOnlineInvitation,
      isSuiteUpsellEnabled:
        site.siteDetails?.matchingSuitePage === TestValues.Experiment,
      isConnectingHouseholdMembers:
        site.siteDetails?.connectHouseholdMembers === TestValues.Experiment,
      isUsingNewComponents: isUsingNewComponents(site),
      isUsingNewLinkRegistryFlow:
        site.siteDetails?.newLinkRegistry === TestValues.Experiment,
      isUsingStripeRegistries:
        site.siteDetails?.stripeRegistry === TestValues.Experiment,
      isWeddingSite: site.isWedding,
      siteType: site.siteType,
      lastNewContactsBannerDismissedDate:
        site.lastNewContactsBannerDismissedDate,
    }),
    [site, isMobile],
  );

  return (
    <StaticSiteDataContext.Provider value={providerValue}>
      {children}
    </StaticSiteDataContext.Provider>
  );
};

/**
 * Hook to check if the OI Messages tab is enabled for this site.
 * The flag value is captured at site creation time and stored in site_details.
 */
export const useOIMessagesTabEnabled = (): boolean => {
  const { isOIMessagesTabEnabled } = useContext(StaticSiteDataContext);
  return isOIMessagesTabEnabled;
};

/**
 * Custom hooks to get experience flags.
 * They must be used inside a LaunchDarklyProvider.
 */
export const useNewMobileExperience = (): boolean => {
  const { isOnlineInvitationSite } = useContext(StaticSiteDataContext);
  const mediumMediaQueryState = useMediaQueryState({
    mediaQuerySize: "medium",
  });
  return mediumMediaQueryState === "BELOW" && isOnlineInvitationSite;
};

export const useNewDesktopExperience = (
  sectionEditorExperience: TestValues | null,
): boolean => {
  const { isOnlineInvitationSite } = useContext(StaticSiteDataContext);
  const mediumMediaQueryState = useMediaQueryState({
    mediaQuerySize: "medium",
  });

  if (!isOnlineInvitationSite) {
    return false;
  }

  if (mediumMediaQueryState === "BELOW") {
    return false;
  }

  return sectionEditorExperience === TestValues.Experiment;
};

export const useGuestHouseholdMembersFlag = (): boolean => {
  const { isConnectingHouseholdMembers } = useContext(StaticSiteDataContext);

  return isConnectingHouseholdMembers;
};

/**
 * Hook to check if the new link vs cash registry workflow is enabled.
 * When true, shows the Gift Registry picker and Link Registry header flow.
 */
export const useNewLinkRegistryFlow = (): boolean => {
  const { isUsingNewLinkRegistryFlow: value } = useContext(
    StaticSiteDataContext,
  );
  return value;
};

export const useStripeRegistries = (): boolean => {
  const { isUsingStripeRegistries: value } = useContext(StaticSiteDataContext);
  return value;
};

export const useIsClickToEditEnabled = (): boolean => {
  const { isOnlineInvitationSite, createdAt } = useContext(
    StaticSiteDataContext,
  );
  const ldClient = useLDClient();
  const smallMediaQueryState = useMediaQueryState({
    mediaQuerySize: "small",
  });

  if (isOnlineInvitationSite) {
    return true;
  } else if (smallMediaQueryState === "BELOW") {
    return false;
  } else {
    // WW click-to-edit is desktop only
    const lowerCutoffDate = new Date("2026-01-27T12:00:00");
    const upperCutoffDate = new Date("2026-03-05T12:00:00");
    const siteCreatedAt = new Date(createdAt);

    // Include only sites created between cutoff dates in experiment
    if (siteCreatedAt < lowerCutoffDate || siteCreatedAt > upperCutoffDate) {
      return false;
    }

    const flagValue = ldClient
      ? ldClient.variation(FRONTEND_FLAGS.weddingClickToEdit, false)
      : false;

    return flagValue;
  }
};

/**
 * Hook to check if the Suite Upsell experience is enabled.
 * The flag value is captured at site creation time and stored in site_details.
 */
export const useSuiteUpsellFlag = (): boolean => {
  const { isSuiteUpsellEnabled } = useContext(StaticSiteDataContext);
  return isSuiteUpsellEnabled;
};

/**
 * Hook to check if automatic party-size parsing from contact names is enabled.
 */
export const useAutoParseContactNameFlag = (): boolean => {
  const ldClient = useLDClient();
  return ldClient
    ? ldClient.variation(FRONTEND_FLAGS.autoParseContactName, false)
    : false;
};

/**
 * Hook to check if the adult/child count RSVP experiment (which includes the
 * "Allow Maybe RSVPs" toggle) is enabled. Only sites created on or after the
 * experiment launch date are eligible for enrollment.
 */
export const useAdultChildMaybeOptionsEnabled = (): boolean => {
  const { createdAt } = useContext(StaticSiteDataContext);
  const ldClient = useLDClient();

  // UTC midnight — aligns with ADULT_CHILD_MAYBE_OPTIONS_EXPERIMENT_LAUNCH_UTC in serializers.py
  const experimentLaunchDate = new Date("2026-04-08T00:00:00Z");
  if (
    config.get("sentryEnvironment") === "prod" &&
    new Date(createdAt) < experimentLaunchDate
  ) {
    return false;
  }
  const flagValue = ldClient
    ? ldClient.variation(
        FRONTEND_FLAGS.adultChildMaybeOptions,
        TestValues.Control,
      )
    : TestValues.Control;
  return flagValue === TestValues.Experiment;
};
